import React, { useLayoutEffect, useRef, useState } from 'react'
import { MenuList, MenuItem, SxProps, Theme, Box, Tooltip } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'

import { navHover, navSelectedSubtle } from '../../../theme/navChrome'

export type SideNavItem = {
  label: string
  /** Stable identifier for the item; passed back to `onChange` when clicked. */
  value: string
  /** Hide the item when explicitly `false` (defaults to shown). */
  visible?: boolean
  /** Disable the row (not clickable, dimmed) — e.g. a section the user lacks
   * permission for. Pair with `tooltip` to name the missing permission. */
  disabled?: boolean
  /** Tooltip shown on hover — used to explain why a `disabled` row is unavailable. */
  tooltip?: string
  /** Optional trailing adornment (count, chevron…); rendered right-aligned. */
  endAdornment?: React.ReactNode
  /**
   * Optional leading glyph. OPTIONAL and additive: a nav that passes none renders
   * exactly as it did before (the label simply becomes the only child of the
   * left group), so Internal Labs is unaffected by Settings adopting icons.
   */
  icon?: React.ReactNode
}

/**
 * How many lines a rail label may occupy before it is clipped.
 *
 * Two, not one: these are documentation HEADINGS, and a heading is the thing that says what
 * the row IS — losing it loses the row's meaning entirely (§49 makes the same argument for
 * table headers). Wrapping is the natural behaviour for a heading in a navigation list, so
 * the fix for a long title is to give it a second line, not to shave it.
 *
 * Not unbounded, though: the internal docs set already carries a 52-character title, and a
 * column where one row is four lines tall stops reading as a list. Two lines covers every
 * title in both artifacts today except that one, which takes the tooltip below.
 */
const RAIL_LABEL_LINES = 2

/**
 * A rail row's label: wraps, clamps at two lines, and carries a tooltip only when the clamp
 * actually bit.
 *
 * ## The defect this exists to fix
 *
 * MUI's `MenuItem` sets `white-space: nowrap` on its root, and nothing here overrode it or
 * set `overflow`. So a label wider than its column did not truncate — it ESCAPED, rendering
 * at full intrinsic width straight over whatever sat to the right. Measured on /internal/docs
 * at 1280x800: 10 of 15 rows overflowed a 153px text box, the worst by 227px, putting its
 * text 153px INSIDE the prose column. That is why the answer is containment rather than a
 * scrolling/marquee treatment — a marquee animates text that is still escaping its box, and
 * moving text is a WCAG 2.2.2 problem on top of not fixing anything.
 *
 * ## Why the tooltip is measured rather than always-on
 *
 * A tooltip on every row would fire on the ~90% of labels that fit, which trains people to
 * ignore it. `scrollHeight > clientHeight` asks the browser the only question that matters —
 * did THIS label, at THIS column width, lose text — so the affordance appears exactly where
 * something is hidden. It re-measures on resize because the column narrows with the viewport
 * and a label that fits at 1440 can clip at 1280.
 *
 * The measured node is the INNER span and the tooltip anchors to the OUTER one. That is not
 * stylistic: `Tooltip` clones its child to attach its own ref, which would overwrite a ref
 * placed on the same element and leave the measurement permanently stale. Keeping the
 * structure identical in both states also means the node is never remounted when `clipped`
 * flips, so the effect's measurement stays valid.
 */
const RailLabel = ({ label }: { label: string }) => {
  const ref = useRef<HTMLSpanElement>(null)
  const [clipped, setClipped] = useState(false)

  useLayoutEffect(() => {
    const measure = () => {
      const el = ref.current
      if (!el) return
      // 1px of slack: sub-pixel line heights make scrollHeight exceed clientHeight by a
      // fraction on rows that are not actually clipped.
      setClipped(el.scrollHeight - el.clientHeight > 1)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [label])

  return (
    <Tooltip title={clipped ? label : ''} placement="right">
      <Box component="span" sx={{ display: 'block', minWidth: 0 }}>
        <Box
          component="span"
          ref={ref}
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: RAIL_LABEL_LINES,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            // Overrides MenuItem's root `nowrap`, which is the actual bug.
            whiteSpace: 'normal',
            // `anywhere` rather than `break-word`: a single unbroken token longer than the
            // column (a slug, a URL) still escapes under `break-word`, which is the same
            // failure in a rarer costume.
            overflowWrap: 'anywhere',
            lineHeight: 1.3,
          }}
        >
          {label}
        </Box>
      </Box>
    </Tooltip>
  )
}

export interface SideNavProps {
  /** Items rendered top-to-bottom. Items with `visible === false` are skipped. */
  items: SideNavItem[]
  /** `value` of the currently-selected item. */
  value: string
  /** Called with an item's `value` when its row is clicked. */
  onChange: (value: string) => void
  /** Extra styles merged onto the MenuList (e.g. sticky positioning). */
  sx?: SxProps<Theme>
  /**
   * Layout axis. `vertical` (default) = the full-width rounded-row sidebar.
   * `horizontal` = a scrollable strip of pill items, for narrow/mobile layouts
   * where the vertical list would cramp the page.
   */
  direction?: 'vertical' | 'horizontal'
  /**
   * Row treatment for the vertical layout. Ignored when `direction="horizontal"`.
   *
   * `pill` (default) — 48px rows, the selected row taking the `primary[50]` surface. Right
   * for a nav of ~10 top-level destinations that are unordered peers: Settings, Labs.
   *
   * `rail` — 28px rows on a continuous 1px rule, the selected row marked by a 2px brand
   * segment on that rule. For a long GROUPED list where the question is "where am I in this
   * structure" rather than "which of these do I want" — a documentation contents list, 17
   * entries across 5 sections and growing.
   *
   * Why a variant and not a second component. The pill is what forces the height: a filled
   * surface needs internal padding to look deliberate, so 48px is not tunable downward
   * while it stays a pill, whereas a rail puts the marker OUTSIDE the text box and lets a
   * row sit at its own line height (48 → 28 here, 1211px → ~760px over 17 rows). And on a
   * docs page the right-hand contents list already marks position with a rail, so a pill on
   * the left would put two visual languages on one screen doing one job.
   */
  variant?: 'pill' | 'rail'
  /**
   * Row height for the `rail` variant, in px. Defaults to 38 — the tallest the settings nav fits
   * in at 1280x800 (15 rows + 72px of section chrome against a 647px budget).
   *
   * Pass a larger value to trade fit for comfort: 48 overflows that budget by ~145px and scrolls.
   */
  rowHeight?: number
  /**
   * Draw the continuous 1px rule behind the rail. Default true.
   *
   * `false` leaves only the 2px brand segment on the selected row. The rule is what makes a rail
   * read as ONE structure with parts rather than a set of separate rows, so dropping it trades that
   * grouping cue for less chrome.
   */
  rule?: boolean
}

/**
 * Vertical in-page navigation menu — the left sub-nav shared by the Settings
 * page and Internal Labs. Full-width rounded rows; the selected row takes the
 * `primary[50]` surface. Single source of truth for this pattern in `@repo/ui`.
 *
 * Rows take an OPTIONAL leading `icon`. It was text-only until VRG-248; the
 * option is additive rather than required so a nav that wants no icons (Internal
 * Labs) keeps its current rendering, and a nav that adopts them (Settings) does
 * so for EVERY row — a half-iconned list reads as unfinished.
 */
export const SideNav = (props: SideNavProps) => {
  const {
    items,
    value,
    onChange,
    sx,
    direction = 'vertical',
    variant = 'pill',
    rowHeight = 38,
    rule = true,
  } = props
  const theme = useTheme()
  const rail = variant === 'rail'

  const shown = items.filter(item => item.visible !== false)

  // Horizontal: a single scrollable row of pill items (mobile sub-nav). Selected
  // pill takes the primary[50] surface; the row scrolls sideways past the edge so
  // a long menu never wraps or grows the header.
  //
  // Deliberately IGNORES `icon`. This strip already overflows horizontally with
  // ten company sections; widening every pill by a glyph plus its gap would push
  // more of the list off-screen, and the icon's job — scanning a vertical column
  // — does not apply to a row the user swipes through one label at a time.
  if (direction === 'horizontal') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          overflowX: 'auto',
          // hide the scrollbar — the overflow is discoverable by drag/swipe
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          ...sx,
        }}
      >
        {shown.map(item => {
          const selected = item.value === value
          return (
            <Box
              key={item.value}
              onClick={() => onChange(item.value)}
              sx={{
                flexShrink: 0,
                cursor: 'pointer',
                px: 3,
                py: 2,
                borderRadius: 2,
                fontSize: 15,
                whiteSpace: 'nowrap',
                color: selected ? theme.palette.text.primary : theme.palette.text.secondary,
                // navChrome. This said `primary[50]` and NEVER rendered: the theme-level
                // MuiMenuItem override lands later in the cascade at equal specificity and won,
                // so this nav has been showing a brand tint while its own source claimed grey.
                backgroundColor: selected ? navSelectedSubtle(theme) : 'transparent',
                transition: 'background-color 0.15s ease, color 0.15s ease',
                '&:hover': { backgroundColor: selected ? navSelectedSubtle(theme) : navHover(theme) },
              }}
            >
              {item.label}
            </Box>
          )
        })}
      </Box>
    )
  }

  return (
    <MenuList
      sx={{
        p: 0,
        borderRadius: 2,
        // The rail itself: one continuous rule the whole group hangs off, with each row's
        // own 2px segment overlapping it by that 1px. Same construction as the docs
        // table-of-contents rail on the opposite side of the page, deliberately.
        // `text.primary` at 16% rather than `grey[100]`. grey[100] resolves to #27272a,
        // which on the #09090b page is a 30-unit step — technically a rail, visually
        // absent, so the rows read as merely indented. Deriving it from the text colour
        // also means it inverts correctly in light mode instead of needing a second token.
        ...(rail && {
          borderLeft: rule ? `1px solid ${alpha(theme.palette.text.primary, 0.16)}` : undefined,
          borderRadius: 0,
        }),
        ...sx,
      }}
    >
      {items
        .filter(item => item.visible !== false)
        .map(item => {
          const selected = item.value === value
          const menuItem = (
            <MenuItem
              key={item.value}
              disableRipple
              selected={selected}
              disabled={item.disabled}
              onClick={() => {
                if (item.disabled) return
                onChange(item.value)
              }}
              sx={
                rail
                  ? {
                      py: 1,
                      pl: 3,
                      pr: 2,
                      borderRadius: 0,
                      mb: 0,
                      // 34px and 15px: a documentation contents list is read, not just
                      // clicked, so the rows carry more size and more air than a rail's
                      // minimum would allow — 15px text in a 34px row leaves the leading
                      // the reference layout has. Still well under the pill's 48.
                      // 38px — the TALLEST row height the settings nav fits in at 1280x800.
                      // Derived, not chosen: the sectioned nav renders 15 rows plus 2 section
                      // headers, whose chrome is a fixed 72px, against a 647px budget (594px of
                      // column plus the 53px tab row the sectioned layout reclaims). 15 x 38 + 72 =
                      // 642. 39px totals 657 and overflows by 10.
                      //
                      // KNOWN COST: this spends the headroom. At 38px there are 5px spare, so the
                      // NEXT nav entry forces either a scroll or a smaller row; 34px left 65px and
                      // absorbed one more. Deliberate — taller rows were asked for over headroom —
                      // but §8 means new entries do arrive, so expect to revisit this number rather
                      // than assume it is permanent.
                      // minHeight, NOT a fixed height. A row whose label wraps to two lines
                      // has to be allowed to grow; pinned at `rowHeight` the second line was
                      // simply painted outside the row. Single-line rows are unchanged — they
                      // still measure exactly `rowHeight` — so the settings nav's height
                      // budget documented above is untouched.
                      minHeight: rowHeight,
                      fontSize: 15,
                      justifyContent: 'space-between',
                      // The row is the clipping boundary of last resort. Even with the label
                      // clamped, this guarantees nothing in a rail row can paint over the
                      // column beside it.
                      overflow: 'hidden',
                      // The rail segment overlaps the group's rule by 1px, so the 2px brand
                      // mark sits ON the line rather than beside it.
                      ml: '-1px',
                      borderLeft: `2px solid ${selected ? theme.palette.brand[600] : 'transparent'}`,
                      // Rail AND a faint surface on the selected row, not the rail alone. At
                      // 30px a low-alpha tint does not read as a pill — it reads as the row
                      // being lit — and it makes the current page findable in peripheral
                      // vision, which a 2px mark on its own does not.
                      backgroundColor: selected ? alpha(theme.palette.brand[500], 0.08) : 'transparent',
                      color: selected ? theme.palette.text.primary : theme.palette.text.secondary,
                      fontWeight: selected ? 600 : 400,
                      '&:hover': {
                        backgroundColor: selected
                          ? alpha(theme.palette.brand[500], 0.08)
                          : alpha(theme.palette.brand[500], 0.04),
                        color: theme.palette.text.primary,
                      },
                      '&.Mui-selected, &.Mui-selected:hover': {
                        backgroundColor: alpha(theme.palette.brand[500], 0.08),
                      },
                    }
                  : {
                      py: 2,
                      px: 4,
                      borderRadius: 2,
                      mb: 1,
                      // Row height matches the standard input field (48px) — the smaller
                      // of {input 48, primary app sidebar 64}, so the in-page sub-nav
                      // still reads as a lower (within-page) navigation level than the
                      // app sidebar while giving each row comfortable click height.
                      minHeight: 48,
                      height: 48,
                      fontSize: 16,
                      justifyContent: 'space-between',
                      backgroundColor: selected ? theme.palette.primary[50] : undefined,
                    }
              }
            >
              {/* 12px icon-to-label, the same gap every menu row in the app uses
                  (§29 on the 4px base) — a sub-nav row and a kebab row should not
                  space their glyphs differently. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
                {item.icon && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0,
                      // Dimmer than the label at rest, full-strength on the selected
                      // row: the icon is a locator, not a second label competing with
                      // the word beside it.
                      color: selected ? theme.palette.text.primary : theme.palette.text.secondary,
                    }}
                  >
                    {item.icon}
                  </Box>
                )}
                {/* Only the rail wraps. The pill variant is a short list of top-level
                    destinations at a fixed 48px, where a wrapping label would break the row
                    rhythm for a case that does not arise; leaving it on `nowrap` also keeps
                    this change scoped to the variant with the measured defect. */}
                {rail ? <RailLabel label={item.label} /> : item.label}
              </Box>
              {item.endAdornment}
            </MenuItem>
          )
          // A disabled MUI element swallows pointer events, so wrap it in a span
          // for the Tooltip to anchor to (names the missing permission — §27).
          if (item.disabled && item.tooltip) {
            return (
              <Tooltip key={item.value} title={item.tooltip} placement="right">
                <span style={{ display: 'block' }}>{menuItem}</span>
              </Tooltip>
            )
          }
          return menuItem
        })}
    </MenuList>
  )
}
